import React, { useCallback, useEffect, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Checkbox,
  Empty,
  Form,
  Input,
  Modal,
  Pagination,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, TeamOutlined, InboxOutlined, UserOutlined } from '@ant-design/icons';
import { serviceBookingOrderService, type AssignableStaffDateItem, type AssignableStaffListItem } from '../../services/serviceBookingOrderService';
import { serviceBookingOrderPoolService, type ServiceBookingOrderPoolItem } from '../../services/serviceBookingOrderPoolService';
import { useHorizontalTableWheel } from '../../hooks/useHorizontalTableWheel';
import { useAppSelector } from '../../store';

const { Option } = Select;

const getDefaultAssignOrderItemIds = (items?: AssignableStaffDateItem[]) => {
  const list = (items || []).filter(item => item.orderItemId);
  const withoutConflict = list.filter(item => !item.hasTimeConflict).map(item => item.orderItemId);
  return withoutConflict.length ? withoutConflict : list.map(item => item.orderItemId);
};

const payStatusMap: Record<string, { color: string; text: string }> = {
  unpaid: { color: 'default', text: '待支付' },
  paid: { color: 'success', text: '已支付' },
};

const poolSourceMap: Record<string, { color: string; text: string }> = {
  system: { color: 'blue', text: '系统入池' },
  admin_create: { color: 'purple', text: '后台手动添加' },
  pending_pool_scheduler: { color: 'cyan', text: '待领取定时同步' },
  pending_pool_schedul: { color: 'cyan', text: '待领取定时同步' },
  timeout_prepare: { color: 'orange', text: '超时任务同步' },
  timeout_cleanup: { color: 'orange', text: '超时清理同步' },
  provider_reject: { color: 'orange', text: '服务人员拒绝' },
  admin_manual: { color: 'purple', text: '后台手动派单' },
  admin_cancel: { color: 'purple', text: '后台取消派单' },
  admin_cancel_dispatch: { color: 'purple', text: '后台取消派单' },
};

const renderTag = (value: string | number, mapper: Record<string, { color: string; text: string }>) => {
  const key = String(value ?? '');
  const config = mapper[key] || { color: 'default', text: key || '-' };
  return <Tag color={config.color}>{config.text}</Tag>;
};

const renderPoolSourceTag = (record: ServiceBookingOrderPoolItem) => {
  const source = String(record.source ?? '');
  const dispatchRemark = String(record.dispatchRemark ?? '');

  if (source.startsWith('pending_pool_schedul')) {
    return <Tag color="cyan">待领取定时同步</Tag>;
  }

  if (source === 'provider_reject' && dispatchRemark.includes('超时')) {
    return <Tag color="orange">超时未接单</Tag>;
  }

  return renderTag(source, poolSourceMap);
};

const formatStaffDistance = (distanceKm?: number | null) => {
  if (typeof distanceKm !== 'number' || !Number.isFinite(distanceKm)) {
    return '距离未知';
  }
  return `距离${distanceKm.toFixed(1)}km`;
};

const isApprovedCertification = (status?: string) => ['approved', 'verified', 'passed', 'success'].includes(String(status || '').trim().toLowerCase());

const normalizeAssignReason = (value?: string) => {
  const text = String(value || '').trim();
  if (!text || /^[?\s]+$/.test(text) || text.includes('????')) {
    return '后台手动派单';
  }
  return text;
};

const fallbackServiceCategoryNames: Record<string, string> = {
  service_category_wechat_home: '微信上门服务',
  service_category_meituan_local: '美团本地服务',
};

const formatServiceProjectsText = (value?: string) => {
  const raw = String(value || '').trim();
  if (!raw) {
    return '-';
  }
  return raw
    .split(/[,，、;\s]+/)
    .map(item => item.trim())
    .filter(Boolean)
    .map(item => fallbackServiceCategoryNames[item] || item)
    .join('、') || '-';
};

type AssignableStaffFilters = {
  address: string;
  staffName: string;
  staffPhone: string;
  requireCertification: boolean;
  requireOrderMode: boolean;
  requireServiceCategory: boolean;
  requireServiceTime: boolean;
  requireDispatchDistance: boolean;
  excludeTimeConflict: boolean;
  excludeRejectedCurrentOrder: boolean;
  excludeTimedOutCurrentOrder: boolean;
};

const defaultAssignableStaffFilters: AssignableStaffFilters = {
  address: '',
  staffName: '',
  staffPhone: '',
  requireCertification: true,
  requireOrderMode: true,
  requireServiceCategory: true,
  requireServiceTime: true,
  requireDispatchDistance: true,
  excludeTimeConflict: true,
  excludeRejectedCurrentOrder: false,
  excludeTimedOutCurrentOrder: false,
};

const renderPoolServiceTimes = (record: ServiceBookingOrderPoolItem) => {
  const serviceTimes = Array.isArray(record.serviceTimes)
    ? record.serviceTimes.filter(item => item?.serviceDate || item?.serviceTimeDesc)
    : [];

  if (serviceTimes.length > 0) {
    return serviceTimes.map(item => [item.serviceDate, item.serviceTimeDesc].filter(Boolean).join(' ')).join('、');
  }

  const dateText = `${record.serviceDateStart || record.serviceDate || '-'} 至 ${record.serviceDateEnd || record.serviceDate || '-'}`;
  const timeText = String(record.serviceTimeDesc || '').trim();
  if (!timeText || timeText === '多时段') return dateText;
  return `${dateText} ${timeText}`;
};

const OrderPoolPage: React.FC = () => {
  const { user } = useAppSelector(state => state.auth);
  const [form] = Form.useForm();
  const [assignForm] = Form.useForm<{ assignedStaffId: string; assignReason: string; noNeedStaffAccept: boolean }>();
  const tableWrapRef = useHorizontalTableWheel();
  const [loading, setLoading] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [list, setList] = useState<ServiceBookingOrderPoolItem[]>([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffOptions, setStaffOptions] = useState<AssignableStaffListItem[]>([]);
  const [staffPagination, setStaffPagination] = useState({ page: 1, pageSize: 10, total: 0 });
  const [staffFilters, setStaffFilters] = useState<AssignableStaffFilters>(defaultAssignableStaffFilters);
  const [currentRow, setCurrentRow] = useState<ServiceBookingOrderPoolItem | null>(null);
  const [assignVisible, setAssignVisible] = useState(false);

  const loadList = useCallback(async (page = 1, pageSize = 10, params: Record<string, unknown> = {}) => {
    setLoading(true);
    try {
      const queryParams: Record<string, unknown> = { ...params };
      if (user?.isProvider && user.id) {
        queryParams.serviceProviderId = user.id;
      }
      const response = await serviceBookingOrderPoolService.list({ page, pageSize, ...queryParams });
      setList(response.list || []);
      setPagination({
        page: response.page || page,
        pageSize: response.pageSize || pageSize,
        total: response.total || 0,
      });
    } catch {
      message.error('加载订单池失败');
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.isProvider]);

  const loadStaffOptions = useCallback(async (
    orderId: string,
    orderItemId?: string,
    page = 1,
    pageSize = 10,
    filters: AssignableStaffFilters = staffFilters,
  ) => {
    setStaffLoading(true);
    try {
      const response = await serviceBookingOrderService.listAssignableStaff(orderId, {
        orderItemId,
        page,
        pageSize,
        onlyAssignable: false,
        ...filters,
      });
      setStaffOptions(response.list || []);
      setStaffPagination({
        page: response.page || page,
        pageSize: response.pageSize || pageSize,
        total: response.total || 0,
      });
    } catch {
      setStaffOptions([]);
      setStaffPagination(prev => ({ ...prev, page, pageSize, total: 0 }));
      message.error('加载可派服务人员失败');
    } finally {
      setStaffLoading(false);
    }
  }, [staffFilters]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleSearch = () => {
    loadList(1, pagination.pageSize, form.getFieldsValue());
  };

  const handleReset = () => {
    form.resetFields();
    loadList(1, pagination.pageSize);
  };

  const openAssignModal = (record: ServiceBookingOrderPoolItem) => {
    setCurrentRow(record);
    setStaffOptions([]);
    setStaffPagination({ page: 1, pageSize: 10, total: 0 });
    setStaffFilters(defaultAssignableStaffFilters);
    assignForm.setFieldsValue({
      assignedStaffId: record.assignedStaffId || undefined,
      assignReason: normalizeAssignReason(record.dispatchRemark),
      noNeedStaffAccept: true,
    });
    setAssignVisible(true);
    loadStaffOptions(record.orderId, undefined, 1, 10, defaultAssignableStaffFilters);
  };

  const handleAssignSubmit = async (staffId?: string, orderItemIds?: string[], noNeedStaffAccept?: boolean) => {
    if (!currentRow) return;
    try {
      const values = await assignForm.validateFields(['assignReason']);
      const assignedStaffId = staffId || assignForm.getFieldValue('assignedStaffId');
      if (!assignedStaffId) {
        message.warning('请选择服务人员');
        return;
      }
      setAssignSubmitting(true);
      await serviceBookingOrderPoolService.assign(currentRow.orderId, {
        assignedStaffId,
        assignReason: values.assignReason,
        orderItemIds,
        noNeedStaffAccept,
      });
      message.success('派单成功');
      setAssignVisible(false);
      setCurrentRow(null);
      loadList(pagination.page, pagination.pageSize, form.getFieldsValue());
    } catch {
      // ignore
    } finally {
      setAssignSubmitting(false);
    }
  };

  const handleStaffFilterChange = (key: keyof AssignableStaffFilters, checked: boolean) => {
    const nextFilters = { ...staffFilters, [key]: checked };
    setStaffFilters(nextFilters);
    if (currentRow) {
      loadStaffOptions(currentRow.orderId, undefined, 1, staffPagination.pageSize, nextFilters);
    }
  };

  const handleStaffTextFilterChange = (key: 'address' | 'staffName' | 'staffPhone', value: string) => {
    const nextFilters = { ...staffFilters, [key]: value };
    setStaffFilters(nextFilters);
    if (currentRow) {
      loadStaffOptions(currentRow.orderId, undefined, 1, staffPagination.pageSize, nextFilters);
    }
  };

  const handleStaffPageChange = (page: number, pageSize: number) => {
    if (currentRow) {
      loadStaffOptions(currentRow.orderId, undefined, page, pageSize, staffFilters);
    }
  };

  const handleStaffCardClick = (staff: AssignableStaffListItem) => {
    const dateItems = (staff.assignableDateItems || []).filter(item => item.orderItemId);
    const defaultChecked = getDefaultAssignOrderItemIds(dateItems);
    let selectedOrderItemIds = [...defaultChecked];
    let noNeedStaffAccept = true;
    Modal.confirm({
      title: '确认派单？',
      width: 760,
      content: (
        <Space orientation="vertical" size={12} style={{ width: '100%' }}>
          <div>确定将勾选的子单派给 {staff.name || staff.username || '该服务人员'} 吗？</div>
          <Space>
            <Switch
              size="small"
              defaultChecked
              onChange={checked => {
                noNeedStaffAccept = checked;
              }}
            />
            <span>无需服务人员同意接单</span>
          </Space>
          {dateItems.length ? (
            <Checkbox.Group
              defaultValue={defaultChecked}
              style={{ width: '100%' }}
              onChange={values => {
                selectedOrderItemIds = values.map(value => String(value));
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: 20, rowGap: 10 }}>
                {dateItems.map(item => (
                  <Checkbox key={item.orderItemId} value={item.orderItemId}>
                    <Space size={8} wrap>
                      <span>{item.label || `${item.serviceDate} ${item.serviceTimeDesc || ''}`}</span>
                      {item.hasTimeConflict ? <Tag color="orange">时间冲突</Tag> : <Tag color="green">无冲突</Tag>}
                    </Space>
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          ) : (
            <span style={{ color: '#999' }}>当前没有可派子单日期</span>
          )}
        </Space>
      ),
      okText: '确认派单',
      cancelText: '取消',
      onOk: () => {
        if (!selectedOrderItemIds.length) {
          message.warning('请选择要派单的日期');
          return Promise.reject();
        }
        assignForm.setFieldsValue({ assignedStaffId: staff.id });
        return handleAssignSubmit(staff.id, selectedOrderItemIds, noNeedStaffAccept);
      },
    });
  };

  const renderAssignableDateTags = (dates?: string[]) => {
    const list = (dates || []).filter(Boolean);
    if (!list.length) {
      return <span style={{ color: '#999' }}>暂无</span>;
    }
    const visibleDates = list.slice(0, 2);
    const hiddenDates = list.slice(2);
    return (
      <Space size={[4, 4]} wrap>
        {visibleDates.map(date => <Tag key={date} color="blue">{date}</Tag>)}
        {hiddenDates.length ? (
          <Tooltip
            title={(
              <Space orientation="vertical" size={2}>
                {list.map(date => <span key={date}>{date}</span>)}
              </Space>
            )}
          >
            <Tag color="blue" style={{ cursor: 'pointer' }}>+{hiddenDates.length}</Tag>
          </Tooltip>
        ) : null}
      </Space>
    );
  };

  const renderStaffCard = (staff: AssignableStaffListItem) => (
    <Card
      key={staff.id}
      size="small"
      hoverable
      onClick={() => handleStaffCardClick(staff)}
      style={{
        cursor: 'pointer',
        borderColor: staff.assignable ? '#1677ff' : '#d9d9d9',
        background: staff.assignable ? '#f8fbff' : '#fafafa',
      }}
      styles={{ body: { padding: 14 } }}
    >
      <div style={{ display: 'flex', height: '100%', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <Space align="start" style={{ minWidth: 0 }}>
            <Avatar size={46} src={staff.avatar || undefined} icon={<UserOutlined />}>
              {staff.name?.charAt(0)}
            </Avatar>
            <div style={{ minWidth: 0 }}>
              <Space wrap>
                <strong>{staff.name || staff.username || '-'}</strong>
                {staff.assignable ? <Tag color="blue">可派单</Tag> : <Tag>不满足规则</Tag>}
                {staff.hasRejectedCurrentOrder ? <Tag color="red">已拒绝当前订单</Tag> : null}
                {staff.hasTimedOutCurrentOrder ? <Tag color="orange">已超时当前订单</Tag> : null}
              </Space>
              <div style={{ color: '#666', marginTop: 6 }}>
                {staff.phone || '-'} / {staff.serviceRegion || '-'} / {formatStaffDistance(staff.distanceKm)}
              </div>
              <div style={{ color: '#666', marginTop: 4, wordBreak: 'break-all' }}>
                服务分类：{formatServiceProjectsText(staff.serviceProjectsText)}
              </div>
            </div>
          </Space>
          <Button type={staff.assignable ? 'primary' : 'default'} size="small" loading={assignSubmitting}>
            派单
          </Button>
        </div>
        <Space size={[4, 4]} wrap>
          <Tag color={isApprovedCertification(staff.certificationStatus) ? 'green' : 'default'}>认证</Tag>
          <Tag color={staff.orderMode ? 'green' : 'default'}>接单</Tag>
          <Tag color={staff.categoryMatched ? 'green' : 'default'}>分类</Tag>
          <Tag color={staff.serviceTimeMatched ? 'green' : 'default'}>时间</Tag>
          <Tag color={staff.dispatchDistanceMatched ? 'green' : 'default'}>距离{staff.maxDispatchDistanceKm ? `≤${staff.maxDispatchDistanceKm}km` : ''}</Tag>
          <Tag color={staff.hasTimeConflict ? 'red' : 'green'}>{staff.hasTimeConflict ? '有冲突' : '无冲突'}</Tag>
        </Space>
        <div>
          <span style={{ color: '#666', marginRight: 8 }}>可派日期：</span>
          {renderAssignableDateTags(staff.assignableDates)}
        </div>
        <div style={{ paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>同时段订单：{staff.sameTimeOrderCount || 0}</div>
          {staff.sameTimeOrders?.length ? (
            <Space orientation="vertical" size={4} style={{ width: '100%' }}>
              {staff.sameTimeOrders.slice(0, 3).map(item => (
                <div key={`${item.orderItemId}-${item.serviceDate}-${item.serviceTimeDesc}`} style={{ color: '#666' }}>
                  {item.serviceDate} {item.serviceTimeDesc} / {item.productName || '-'} / {item.orderNo || item.orderItemNo || '-'} / {item.statusText || item.status || '-'}
                </div>
              ))}
            </Space>
          ) : (
            <span style={{ color: '#999' }}>暂无同时段订单</span>
          )}
        </div>
      </div>
    </Card>
  );

  const columns: ColumnsType<ServiceBookingOrderPoolItem> = [
    { title: '订单号', dataIndex: 'orderNo', key: 'orderNo', width: 180 },
    { title: '服务项目', dataIndex: 'productName', key: 'productName', width: 160 },
    {
      title: '宠物数量',
      dataIndex: 'petQuantity',
      key: 'petQuantity',
      width: 100,
      render: (value: number) => `${Number(value || 1)}只`,
    },
    { title: '联系人', dataIndex: 'contactName', key: 'contactName', width: 110 },
    { title: '联系电话', dataIndex: 'contactPhone', key: 'contactPhone', width: 140 },
    {
      title: '服务地址',
      dataIndex: 'addressText',
      key: 'addressText',
      width: 320,
      render: (value: string) => value || '-',
    },
    {
      title: '服务时间',
      key: 'serviceTime',
      width: 300,
      render: (_, record) => renderPoolServiceTimes(record),
    },
    {
      title: '订单金额',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      width: 110,
      render: (value: number) => `¥${Number(value || 0).toFixed(2)}`,
    },
    {
      title: '支付状态',
      dataIndex: 'payStatus',
      key: 'payStatus',
      width: 100,
      render: (value: string) => renderTag(value, payStatusMap),
    },
    {
      title: '订单状态',
      dataIndex: 'statusText',
      key: 'statusText',
      width: 110,
      render: (value: string) => <Tag color="processing">{value || '-'}</Tag>,
    },
    {
      title: '池内状态',
      dataIndex: 'isPai',
      key: 'isPai',
      width: 100,
      render: (value: number) => (value === 1 ? <Tag color="gold">待确认</Tag> : <Tag color="blue">待领取</Tag>),
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (_: string, record) => renderPoolSourceTag(record),
    },
    {
      title: '当前指派',
      dataIndex: 'assignedStaffName',
      key: 'assignedStaffName',
      width: 140,
      render: (value: string, record) => (value ? `${value}${record.assignedStaffPhone ? ` / ${record.assignedStaffPhone}` : ''}` : '-'),
    },
    {
      title: '派单备注',
      dataIndex: 'dispatchRemark',
      key: 'dispatchRemark',
      width: 240,
      render: (value: string) => value || '-',
    },
    {
      title: '入池时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 170,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 170,
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button type="link" size="small" icon={<TeamOutlined />} onClick={() => openAssignModal(record)}>
          手动派单
        </Button>
      ),
    },
  ];

  return (
    <Card title={<><InboxOutlined /> 订单池管理</>}>
      <Form form={form} layout="inline" style={{ marginBottom: 16 }} onFinish={handleSearch}>
        <Form.Item name="orderNo">
          <Input placeholder="订单号" allowClear style={{ width: 180 }} />
        </Form.Item>
        <Form.Item name="productName">
          <Input placeholder="服务项目" allowClear style={{ width: 150 }} />
        </Form.Item>
        <Form.Item name="contactName">
          <Input placeholder="联系人" allowClear style={{ width: 120 }} />
        </Form.Item>
        <Form.Item name="contactPhone">
          <Input placeholder="联系电话" allowClear style={{ width: 140 }} />
        </Form.Item>
        <Form.Item name="isPai">
          <Select placeholder="池内状态" allowClear style={{ width: 120 }}>
            <Option value={0}>待领取</Option>
            <Option value={1}>待确认</Option>
          </Select>
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>

      <div ref={tableWrapRef}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={list}
          columns={columns}
          scroll={{ x: 2220 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: total => `共 ${total} 条`,
            onChange: (page, pageSize) => loadList(page, pageSize, form.getFieldsValue()),
          }}
        />
      </div>

      <Modal
        title="手动派单"
        open={assignVisible}
        onCancel={() => setAssignVisible(false)}
        footer={null}
        destroyOnHidden
        width={1280}
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item name="assignedStaffId" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="assignReason"
            label="派单说明"
            rules={[{ required: true, message: '请输入派单说明' }]}
          >
            <Input.TextArea rows={3} maxLength={200} showCount placeholder="请输入派单说明" />
          </Form.Item>
        </Form>
        <div style={{ marginBottom: 12 }}>
          <Space size={[12, 8]} wrap style={{ marginBottom: 12 }}>
            <span>查询条件：</span>
            <Input
              allowClear
              placeholder="服务人员姓名"
              style={{ width: 160 }}
              value={staffFilters.staffName}
              onChange={(event) => handleStaffTextFilterChange('staffName', event.target.value)}
            />
            <Input
              allowClear
              placeholder="服务人员电话"
              style={{ width: 160 }}
              value={staffFilters.staffPhone}
              onChange={(event) => handleStaffTextFilterChange('staffPhone', event.target.value)}
            />
            <Input
              allowClear
              placeholder="服务地址"
              style={{ width: 220 }}
              value={staffFilters.address}
              onChange={(event) => handleStaffTextFilterChange('address', event.target.value)}
            />
          </Space>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
            <Space size={[12, 8]} wrap>
              <span>筛选条件：</span>
              <Space><Switch size="small" checked={staffFilters.requireCertification} onChange={checked => handleStaffFilterChange('requireCertification', checked)} /><span>认证通过</span></Space>
              <Space><Switch size="small" checked={staffFilters.requireOrderMode} onChange={checked => handleStaffFilterChange('requireOrderMode', checked)} /><span>开启接单</span></Space>
              <Space><Switch size="small" checked={staffFilters.requireServiceCategory} onChange={checked => handleStaffFilterChange('requireServiceCategory', checked)} /><span>服务分类匹配</span></Space>
              <Space><Switch size="small" checked={staffFilters.requireServiceTime} onChange={checked => handleStaffFilterChange('requireServiceTime', checked)} /><span>服务时间匹配</span></Space>
              <Space><Switch size="small" checked={staffFilters.requireDispatchDistance} onChange={checked => handleStaffFilterChange('requireDispatchDistance', checked)} /><span>派单距离内</span></Space>
              <Space><Switch size="small" checked={staffFilters.excludeTimeConflict} onChange={checked => handleStaffFilterChange('excludeTimeConflict', checked)} /><span>排除时间冲突</span></Space>
              <Space><Switch size="small" checked={staffFilters.excludeRejectedCurrentOrder} onChange={checked => handleStaffFilterChange('excludeRejectedCurrentOrder', checked)} /><span>排除已拒绝</span></Space>
              <Space><Switch size="small" checked={staffFilters.excludeTimedOutCurrentOrder} onChange={checked => handleStaffFilterChange('excludeTimedOutCurrentOrder', checked)} /><span>排除已超时</span></Space>
            </Space>
            <span style={{ color: '#666' }}>共 {staffPagination.total} 人，每页 10 人</span>
          </div>
        </div>
        <Spin spinning={staffLoading}>
          {staffOptions.length ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
              {staffOptions.map(renderStaffCard)}
            </div>
          ) : (
            <Empty description={staffLoading ? '正在加载服务人员' : '暂无服务人员'} />
          )}
        </Spin>
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Pagination
            current={staffPagination.page}
            pageSize={staffPagination.pageSize}
            total={staffPagination.total}
            showSizeChanger={false}
            onChange={handleStaffPageChange}
          />
        </div>
      </Modal>
    </Card>
  );
};

export default OrderPoolPage;
